import { useState, useEffect, useCallback } from 'react';
import { userEntityLinksApi } from '@/api/api';
import type { UserEntityLinkWatchlistSummary } from '@/types/watchlist';

/**
 * Watchlist Dropdown Summary Hook —— 走 POST /pms/user-entity-links/search
 * 拿 watchlist 的扁平按类型计数 (`{ totalCount, byEntityType: { task: N, ... } }`)。
 *
 * 取代旧 useWatchlist + load_entity_details=true 的重路径(那条路把所有实体
 * 详情都拉回来,只为了客户端 group-by 数数)。
 *
 * 设计要点(跟 useWatchlist 对齐):
 *   - 模块级 cache (5 分钟 TTL),所有挂载本 hook 的组件共享
 *   - In-flight 去重,同时多个组件首次挂载只发 1 次请求
 *   - `invalidateWatchlistSummaryCache()` 写后强制失效,联动 registerWatchlistSummaryRefetch
 *     注册的所有刷新回调
 *   - `enabled` 默认 true;dropdown 类调用方在 popover 打开前传 false,
 *     避免 top-bar 渲染就无脑拉
 *
 * 该 hook 跟 useWatchlist 并存(useWatchlist 仍服务 WatchButton 判断"我是否
 * 关注此 entity"),写操作侧由 invalidateWatchlistCache 联动失效两者。
 */

const CACHE_KEY = 'watchlist_summary';
const CACHE_DURATION = 5 * 60 * 1000;

let cachedData: UserEntityLinkWatchlistSummary | null = null;
let cachedAt = 0;
let inFlight: Promise<UserEntityLinkWatchlistSummary> | null = null;

const refetchCallbacks = new Set<() => void>();

export const registerWatchlistSummaryRefetch = (callback: () => void) => {
  refetchCallbacks.add(callback);
  return () => {
    refetchCallbacks.delete(callback);
  };
};

const triggerAllRefetch = () => {
  refetchCallbacks.forEach((cb) => {
    try {
      cb();
    } catch (error) {
      console.error('Error triggering watchlist summary refetch:', error);
    }
  });
};

export const invalidateWatchlistSummaryCache = () => {
  cachedData = null;
  cachedAt = 0;
  inFlight = null;
  triggerAllRefetch();
};

const isCacheValid = () =>
  cachedData !== null && Date.now() - cachedAt < CACHE_DURATION;

async function fetchSummary(): Promise<UserEntityLinkWatchlistSummary> {
  // 复用已在飞的 promise,避免并发多次请求
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await userEntityLinksApi.search({
        // pageSize=1 + columns="id" → 只为触发 search,真正消费的是 summary
        pageSize: 1,
        columns: 'id',
        includeSummary: true,
        summaryType: 'watchlist',
      });
      // axios-config 自动解 { code, data, msg },response 直接是 data 部分
      // BaseListResponse 形态:{ results, pagination, summary }
      const summary: UserEntityLinkWatchlistSummary = (response as any)?.summary ?? {
        totalCount: 0,
        byEntityType: {},
      };
      // 兜底:summary 可能为 null/undefined(无 watcher 记录时后端可能不返回)
      const normalized: UserEntityLinkWatchlistSummary = {
        totalCount: summary?.totalCount ?? 0,
        byEntityType: summary?.byEntityType ?? {},
      };
      cachedData = normalized;
      cachedAt = Date.now();
      return normalized;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export interface UseWatchlistSummaryReturn {
  data: UserEntityLinkWatchlistSummary | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  totalCount: number;
}

export const useWatchlistSummary = (
  options?: { enabled?: boolean },
): UseWatchlistSummaryReturn => {
  const enabled = options?.enabled !== false;
  const [data, setData] = useState<UserEntityLinkWatchlistSummary | null>(
    cachedData,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) return;
    if (isCacheValid()) {
      setData(cachedData);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSummary();
      setData(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to fetch watchlist summary');
      setError(e);
      console.error('Error fetching watchlist summary:', e);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    void fetchData();
  }, [fetchData, enabled]);

  const refetch = useCallback(() => {
    cachedData = null;
    cachedAt = 0;
    inFlight = null;
    void fetchData();
  }, [fetchData]);

  // 注册全局 refetch 回调:写操作后 invalidateWatchlistSummaryCache 会触发
  useEffect(() => {
    const unregister = registerWatchlistSummaryRefetch(refetch);
    return unregister;
  }, [refetch]);

  return {
    data,
    loading,
    error,
    refetch,
    totalCount: data?.totalCount ?? 0,
  };
};

// Suppress unused warning for CACHE_KEY (kept for symmetry with useWatchlist)
void CACHE_KEY;
